import { useState, useEffect, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { scooterApi, partsApi } from "@/lib/api";
import { toast } from "sonner";
import { Printer, Search, FileText, X, Check, Tag, Bike, Wrench, QrCode, Calendar } from "lucide-react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg";

// Pre-load logo as base64 for PDF embedding
let logoBase64 = null;
async function loadLogo() {
  if (logoBase64) return logoBase64;
  try {
    const resp = await fetch(LOGO_URL);
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        logoBase64 = reader.result;
        resolve(logoBase64);
      };
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

async function generateQRBase64(text) {
  try {
    return await QRCode.toDataURL(text, { width: 100, margin: 1, color: { dark: "#000", light: "#fff" } });
  } catch {
    return null;
  }
}

function truncate(str, max) {
  if (!str) return "-";
  return str.length > max ? str.substring(0, max) + "..." : str;
}

function getDocStatusLabel(scooter) {
  const tf = scooter.technical_file;
  if (tf) {
    if (tf.tip_acte === "carte_strainatate" || tf.tip_acte === "acte_strainatate") return "Acte strainatate";
    if (tf.tip_acte === "fara_acte") return "Doar factura";
    if (tf.tip_acte === "carte_rar") return "Acte Romania";
    if (tf.tara_documente) return tf.tara_documente;
  }
  return "Nespecificat";
}

function getDocStatus(scooter) {
  const tf = scooter.technical_file;
  if (tf) {
    if (tf.tip_acte === "carte_strainatate" || tf.tip_acte === "acte_strainatate") return "Acte strainatate";
    if (tf.tip_acte === "fara_acte") return "Doar factura";
    if (tf.tip_acte === "carte_rar") return "Acte Romania";
    if (tf.tara_documente) return tf.tara_documente;
  }
  return "Nespecificat";
}

function getStatusLabel(status) {
  const map = {
    in_stock: "In stoc",
    reserved: "Rezervat",
    sold: "Vandut",
    in_service: "In service",
  };
  return map[status] || status || "-";
}

function getConditionLabel(condition) {
  const map = {
    new: "Nou",
    good: "Bun",
    used: "Utilizat",
    damaged: "Deteriorat",
    refurbished: "Reconditionat",
  };
  return map[condition] || condition || "-";
}

async function generateScooterPDF(scooters) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const logo = await loadLogo();
  // Pre-generate all QR codes
  const qrCodes = await Promise.all(
    scooters.map(s => generateQRBase64(`SCUTERSH:${s.scooter_id}|${s.brand} ${s.model}|${s.selling_price}EUR`))
  );

  const pageW = 210;
  const pageH = 297;
  const cols = 3;
  const rows = 3;
  const margin = 6;
  const gap = 3;
  const labelW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
  const labelH = (pageH - margin * 2 - gap * (rows - 1)) / rows;

  scooters.forEach((scooter, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = margin + col * (labelW + gap);
    const y = margin + row * (labelH + gap);
    const qr = qrCodes[idx];

    // Outer border
    doc.setDrawColor(30, 30, 30);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, labelW, labelH, 2.5, 2.5);

    // === HEADER BAR ===
    doc.setFillColor(18, 18, 18);
    doc.roundedRect(x, y, labelW, 14, 2.5, 2.5, "F");
    doc.setFillColor(18, 18, 18);
    doc.rect(x, y + 8, labelW, 6, "F");

    // Logo in header - white background for red logo
    if (logo) {
      try {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x + 1.5, y + 1, 10, 10, 1.5, 1.5, "F");
        doc.addImage(logo, "JPEG", x + 2, y + 1.5, 9, 9);
      } catch (e) { console.warn("Logo error", e); }
    }

    // "SCUTERSH" text
    doc.setTextColor(80, 220, 100);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("SCUTERSH", x + 13, y + 6);

    // Brand+Model in header right
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(6.5);
    const titleText = `${scooter.brand} ${scooter.model}`.toUpperCase();
    doc.text(truncate(titleText, 22), x + labelW - 3, y + 6, { align: "right" });

    // === ARTICLE ID BADGE ===
    const artId = scooter.scooter_id || "N/A";
    doc.setFillColor(80, 220, 100);
    const badgeW = Math.max(doc.getTextWidth(artId) * 1.4 + 6, 24);
    doc.roundedRect(x + labelW / 2 - badgeW / 2, y + 16, badgeW, 7, 2, 2, "F");
    doc.setTextColor(10, 10, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(artId, x + labelW / 2, y + 20.5, { align: "center" });

    // === PRICE - LARGE ===
    doc.setTextColor(80, 220, 100);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const priceText = `${scooter.selling_price?.toLocaleString() || "0"} EUR`;
    doc.text(priceText, x + labelW / 2, y + 30, { align: "center" });

    // Thin green line under price
    doc.setDrawColor(80, 220, 100);
    doc.setLineWidth(0.4);
    doc.line(x + 4, y + 32, x + labelW - 4, y + 32);

    // === DATA ROWS (left column, smaller to make room for QR) ===
    const dataW = labelW - 20; // Reserve 18mm for QR
    let cy = y + 36;
    const lineH = 6;
    const leftX = x + 3;
    const valX = x + dataW;

    const fields = [
      { label: "Model", value: `${scooter.brand} ${scooter.model}` },
      { label: "An", value: `${scooter.year || "-"}` },
      { label: "VIN", value: truncate(scooter.vin || "-", 16) },
      { label: "Km", value: `${scooter.mileage?.toLocaleString() || "0"} km` },
      { label: "CC", value: `${scooter.engine_cc || "-"} cc` },
      { label: "Vmax", value: scooter.technical_file?.max_speed || scooter.max_speed || "-" },
      { label: "Acte", value: getDocStatusLabel(scooter) },
      { label: "Stare", value: getStatusLabel(scooter.status) },
    ];

    fields.forEach((field) => {
      doc.setTextColor(130, 130, 130);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      doc.text(field.label, leftX, cy);

      doc.setTextColor(40, 40, 40);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.text(truncate(field.value, 20), valX, cy, { align: "right" });

      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.1);
      doc.line(leftX, cy + 1.5, valX, cy + 1.5);
      cy += lineH;
    });

    // === QR CODE (right side) ===
    if (qr) {
      try {
        doc.addImage(qr, "PNG", x + labelW - 18, y + 36, 15, 15);
      } catch (e) { console.warn("QR error", e); }
    }

    // === FOOTER ===
    doc.setFillColor(18, 18, 18);
    doc.roundedRect(x, y + labelH - 7, labelW, 7, 2.5, 2.5, "F");
    doc.setFillColor(18, 18, 18);
    doc.rect(x, y + labelH - 7, labelW, 3.5, "F");
    doc.setTextColor(80, 220, 100);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("scutersh.store", x + labelW / 2, y + labelH - 2.5, { align: "center" });
  });

  return doc;
}

async function generatePartsPDF(parts) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const logo = await loadLogo();
  const qrCodes = await Promise.all(
    parts.map(p => generateQRBase64(`SCUTERSH:${p.part_id}|${p.name}|${p.price}EUR`))
  );

  const pageW = 210;
  const pageH = 297;
  const cols = 3;
  const rows = 3;
  const margin = 6;
  const gap = 3;
  const labelW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
  const labelH = (pageH - margin * 2 - gap * (rows - 1)) / rows;

  parts.forEach((part, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = margin + col * (labelW + gap);
    const y = margin + row * (labelH + gap);
    const qr = qrCodes[idx];

    // Outer border
    doc.setDrawColor(30, 30, 30);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, labelW, labelH, 2.5, 2.5);

    // === HEADER BAR ===
    doc.setFillColor(18, 18, 18);
    doc.roundedRect(x, y, labelW, 14, 2.5, 2.5, "F");
    doc.setFillColor(18, 18, 18);
    doc.rect(x, y + 8, labelW, 6, "F");

    if (logo) {
      try {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x + 1.5, y + 1, 10, 10, 1.5, 1.5, "F");
        doc.addImage(logo, "JPEG", x + 2, y + 1.5, 9, 9);
      } catch (e) { console.warn("Logo error", e); }
    }

    doc.setTextColor(80, 220, 100);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("SCUTERSH", x + 13, y + 6);

    // Part type indicator
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(5.5);
    doc.text("PIECE / PART", x + labelW - 3, y + 6, { align: "right" });

    // === PART ID BADGE ===
    const artId = part.part_id || "N/A";
    doc.setFillColor(80, 220, 100);
    const badgeW = Math.max(doc.getTextWidth(artId) * 1.4 + 6, 24);
    doc.roundedRect(x + labelW / 2 - badgeW / 2, y + 16, badgeW, 7, 2, 2, "F");
    doc.setTextColor(10, 10, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(artId, x + labelW / 2, y + 20.5, { align: "center" });

    // === PRICE ===
    doc.setTextColor(80, 220, 100);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const priceText = `${part.price?.toLocaleString() || "0"} EUR`;
    doc.text(priceText, x + labelW / 2, y + 30, { align: "center" });

    doc.setDrawColor(80, 220, 100);
    doc.setLineWidth(0.4);
    doc.line(x + 4, y + 32, x + labelW - 4, y + 32);

    // === DATA ROWS (left, room for QR) ===
    const dataW = labelW - 20;
    let cy = y + 36;
    const lineH = 6;
    const leftX = x + 3;
    const valX = x + dataW;

    const fields = [
      { label: "Nume", value: truncate(part.name || "-", 18) },
      { label: "Categorie", value: truncate(part.category || "-", 16) },
      { label: "Nr. Piesa", value: truncate(part.part_number || "-", 16) },
      { label: "Compat.", value: truncate((part.compatible_brands || []).join(", ") || "-", 18) },
      { label: "Stare", value: getConditionLabel(part.condition) },
      { label: "Stoc", value: `${part.quantity || 0} buc.` },
    ];

    fields.forEach((field) => {
      doc.setTextColor(130, 130, 130);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      doc.text(field.label, leftX, cy);

      doc.setTextColor(40, 40, 40);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.text(truncate(field.value, 18), valX, cy, { align: "right" });

      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.1);
      doc.line(leftX, cy + 1.5, valX, cy + 1.5);
      cy += lineH;
    });

    // === QR CODE (right side) ===
    if (qr) {
      try {
        doc.addImage(qr, "PNG", x + labelW - 18, y + 36, 15, 15);
      } catch (e) { console.warn("QR error", e); }
    }

    // === FOOTER ===
    doc.setFillColor(18, 18, 18);
    doc.roundedRect(x, y + labelH - 7, labelW, 7, 2.5, 2.5, "F");
    doc.setFillColor(18, 18, 18);
    doc.rect(x, y + labelH - 7, labelW, 3.5, "F");
    doc.setTextColor(80, 220, 100);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("scutersh.store", x + labelW / 2, y + labelH - 2.5, { align: "center" });
  });

  return doc;
}

export default function LabelsPage() {
  const [tab, setTab] = useState("scooters");
  const [scooters, setScooters] = useState([]);
  const [parts, setParts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const APIBASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scooterData, partsData] = await Promise.all([
        scooterApi.getAll(),
        partsApi.getAll(),
      ]);
      setScooters(scooterData);
      setParts(partsData);
    } catch {
      toast.error("Erreur chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Clear selection when switching tabs
  const switchTab = (newTab) => {
    setTab(newTab);
    setSelected([]);
    setSearch("");
  };

  const items = tab === "parts" ? parts : scooters;
  const getItemId = (item) => tab === "parts" ? item.part_id : item.scooter_id;

  const toggleSelect = (itemId) => {
    setSelected((prev) => {
      if (prev.includes(itemId)) return prev.filter((id) => id !== itemId);
      if (prev.length >= 9) {
        toast.error("Maximum 9 étiquettes par page A4");
        return prev;
      }
      return [...prev, itemId];
    });
  };

  const selectAll = () => {
    const filtered = filteredItems.slice(0, 9);
    setSelected(filtered.map(getItemId));
  };

  const clearSelection = () => setSelected([]);

  const filteredItems = items.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (tab === "scooters") {
      return (
        item.brand?.toLowerCase().includes(q) ||
        item.model?.toLowerCase().includes(q) ||
        item.scooter_id?.toLowerCase().includes(q) ||
        item.vin?.toLowerCase().includes(q)
      );
    }
    return (
      item.name?.toLowerCase().includes(q) ||
      item.part_id?.toLowerCase().includes(q) ||
      item.category?.toLowerCase().includes(q) ||
      item.part_number?.toLowerCase().includes(q)
    );
  });

  const handlePrint = async () => {
    if (selected.length === 0) {
      toast.error("Sélectionnez au moins 1 article");
      return;
    }
    const selectedItems = selected.map((id) => items.find((i) => getItemId(i) === id)).filter(Boolean);
    const doc = tab === "scooters"
      ? await generateScooterPDF(selectedItems)
      : await generatePartsPDF(selectedItems);
    doc.save(`etiquettes_${tab}_scutersh_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`PDF généré avec ${selectedItems.length} étiquette(s) !`);
  };

  const handlePreview = async () => {
    if (selected.length === 0) {
      toast.error("Sélectionnez au moins 1 article");
      return;
    }
    const selectedItems = selected.map((id) => items.find((i) => getItemId(i) === id)).filter(Boolean);
    const doc = tab === "scooters"
      ? await generateScooterPDF(selectedItems)
      : await generatePartsPDF(selectedItems);
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, "_blank");
  };

  // === QR CODE PDF ===
  const handlePrintQR = async () => {
    if (selected.length === 0) { toast.error("Selectati cel putin 1 scuter"); return; }
    const selectedScooters = selected.map(id => scooters.find(s => s.scooter_id === id)).filter(Boolean);
    
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const cols = 3, rows = 3, margin = 6, gap = 3;
    const labelW = (210 - margin * 2 - gap * (cols - 1)) / cols;
    const labelH = (297 - margin * 2 - gap * (rows - 1)) / rows;

    for (let idx = 0; idx < selectedScooters.length; idx++) {
      const s = selectedScooters[idx];
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = margin + col * (labelW + gap);
      const y = margin + row * (labelH + gap);

      // Black background
      doc.setFillColor(18, 18, 18);
      doc.roundedRect(x, y, labelW, labelH, 3, 3, "F");

      // Title: Brand Model Year (white, bold, italic)
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bolditalic");
      doc.setFontSize(11);
      const title = `${s.brand} ${s.model} ${s.year || ""}`.trim();
      doc.text(title, x + labelW / 2, y + 12, { align: "center", maxWidth: labelW - 8 });

      // QR Code (white bg rounded rect + QR)
      const qrSize = labelW - 16;
      const qrX = x + (labelW - qrSize) / 2;
      const qrY = y + 18;

      // White rounded background for QR
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 4, 4, "F");

      // Generate QR
      try {
        const qrUrl = `https://scutersh.store/qr/${s.scooter_id}`;
        const qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 400, margin: 1, color: { dark: "#000000", light: "#ffffff" } });
        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
      } catch (e) { console.warn("QR error", e); }

      // Scooter ID at bottom (white, bold)
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(s.scooter_id, x + labelW / 2, y + labelH - 5, { align: "center" });
    }

    doc.save(`qr_codes_scutersh_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`${selectedScooters.length} QR codes generat!`);
  };

  // === SALES REPORT PDF ===
  const handleSalesReport = async () => {
    if (!dateFrom || !dateTo) { toast.error("Selectati perioada (de la - pana la)"); return; }
    toast.loading("Se genereaza raportul...");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${APIBASE}/api/scooters`, { headers: { Authorization: `Bearer ${token}` } });
      const allScooters = res.data;
      
      // Filter sold scooters in date range
      const sold = allScooters.filter(s => {
        if (s.status !== "sold") return false;
        const saleDate = s.sale_record?.date || s.delivery_info?.date || s.updated_at || "";
        if (!saleDate) return true; // include if no date
        return saleDate >= dateFrom && saleDate <= dateTo + "T23:59:59";
      });

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const w = 297, h = 210, margin = 10;

      // Header
      doc.setFillColor(18, 18, 18);
      doc.rect(0, 0, w, 25, "F");
      doc.setTextColor(80, 220, 100);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("SCUTERSH - Raport vanzari", margin, 12);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text(`Perioada: ${dateFrom} - ${dateTo} | ${sold.length} scutere vandute`, margin, 20);

      // Table
      let y = 32;
      doc.setFillColor(50, 50, 50);
      doc.rect(margin, y, w - margin * 2, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7);
      doc.setFont("helvetica", "bold");
      const cols2 = [0, 8, 50, 70, 95, 130, 155, 195, 230];
      ["Nr", "Scuter", "An", "Nr. Produs", "Client", "Telefon", "Adresa livrare", "Data RAR", "Inclus"].forEach((h2, i) => {
        doc.text(h2, margin + cols2[i] + 1, y + 5);
      });
      y += 7;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      sold.forEach((s, i) => {
        if (y > 195) { doc.addPage(); y = 15; }
        const bg = i % 2 === 0 ? 245 : 255;
        doc.setFillColor(bg, bg, bg);
        doc.rect(margin, y, w - margin * 2, 6, "F");
        doc.setFontSize(6.5);
        const sr = s.sale_record || {};
        const di = s.delivery_info || {};
        doc.text(`${i + 1}`, margin + cols2[0] + 1, y + 4.5);
        doc.text(`${s.brand} ${s.model}`.substring(0, 22), margin + cols2[1] + 1, y + 4.5);
        doc.text(`${s.year || ""}`, margin + cols2[2] + 1, y + 4.5);
        doc.text(`${s.scooter_id}`, margin + cols2[3] + 1, y + 4.5);
        doc.text((sr.client_name || di.client_name || "-").substring(0, 18), margin + cols2[4] + 1, y + 4.5);
        doc.text((sr.client_phone || di.phone || "-").substring(0, 15), margin + cols2[5] + 1, y + 4.5);
        doc.text((di.address || "-").substring(0, 22), margin + cols2[6] + 1, y + 4.5);
        doc.text((di.rar_date || "-").substring(0, 12), margin + cols2[7] + 1, y + 4.5);
        doc.text(di.livrator_status === "rar_acceptat" ? "DA" : di.livrator_status === "rar_refuzat" ? "NU" : "-", margin + cols2[8] + 1, y + 4.5);
        y += 6;
      });

      // Footer
      y += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, w - margin, y);
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(6);
      doc.text(`SCUTERSH - Sura Mare, Sector 4, Bucuresti | +40792250058 | Generat: ${new Date().toLocaleDateString("ro-RO")}`, w / 2, y + 4, { align: "center" });

      doc.save(`raport_vanzari_${dateFrom}_${dateTo}.pdf`);
      toast.dismiss();
      toast.success(`Raport generat: ${sold.length} scutere vandute`);
    } catch (err) {
      toast.dismiss();
      toast.error("Eroare generare raport");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Chargement...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="labels-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-3xl flex items-center gap-3">
              <Tag className="w-8 h-8 text-primary" />
              Étiquettes Produits
            </h1>
            <p className="text-muted-foreground mt-1">
              Selectati pana la 9 articole pentru a genera o pagina A4 cu etichete
            </p>
          </div>
          <div className="flex gap-3">
            {tab !== "sales" && (
              <>
                <Button onClick={handlePreview} variant="outline" disabled={selected.length === 0}>
                  <FileText className="w-4 h-4 mr-2" /> Previzualizare
                </Button>
                {tab === "qr" ? (
                  <Button onClick={handlePrintQR} disabled={selected.length === 0} className="bg-primary text-black font-bold">
                    <QrCode className="w-4 h-4 mr-2" /> Descarca QR ({selected.length}/9)
                  </Button>
                ) : (
                  <Button onClick={handlePrint} disabled={selected.length === 0} className="bg-primary text-black font-bold">
                    <Printer className="w-4 h-4 mr-2" /> Descarca PDF ({selected.length}/9)
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-2 flex-wrap">
          <Button variant={tab === "scooters" ? "default" : "outline"} onClick={() => switchTab("scooters")} className={tab === "scooters" ? "bg-primary text-black" : ""}>
            <Bike className="w-4 h-4 mr-2" /> Etichete ({scooters.length})
          </Button>
          <Button variant={tab === "parts" ? "default" : "outline"} onClick={() => switchTab("parts")} className={tab === "parts" ? "bg-primary text-black" : ""}>
            <Wrench className="w-4 h-4 mr-2" /> Piese ({parts.length})
          </Button>
          <Button variant={tab === "qr" ? "default" : "outline"} onClick={() => switchTab("qr")} className={tab === "qr" ? "bg-primary text-black" : ""}>
            <QrCode className="w-4 h-4 mr-2" /> QR Codes
          </Button>
          <Button variant={tab === "sales" ? "default" : "outline"} onClick={() => setTab("sales")} className={tab === "sales" ? "bg-primary text-black" : ""}>
            <Calendar className="w-4 h-4 mr-2" /> Raport vanzari
          </Button>
        </div>

        {/* Search + Selection controls */}
        {tab !== "sales" && (
        <>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "parts" ? "Cauta piesa..." : "Cauta scuter..."}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll}>
              <Check className="w-4 h-4 mr-1" /> Selecteaza 9
            </Button>
            <Button variant="outline" size="sm" onClick={clearSelection}>
              <X className="w-4 h-4 mr-1" /> Sterge ({selected.length})
            </Button>
          </div>
          <Badge variant="outline" className="text-sm">
            {selected.length}/9 selectate
          </Badge>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selected.map((id) => {
              const item = items.find((i) => getItemId(i) === id);
              if (!item) return null;
              const label = tab === "parts" ? `${item.name} (${id})` : `${item.brand} ${item.model} (${id})`;
              return (
                <Badge key={id} className="bg-primary/20 text-primary cursor-pointer hover:bg-primary/30" onClick={() => toggleSelect(id)}>
                  {label} <X className="w-3 h-3 ml-1" />
                </Badge>
              );
            })}
          </div>
        )}
        </>
        )}

        {/* Items grid */}
        {tab !== "sales" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filteredItems.map((item) => {
            const itemId = getItemId(item);
            const isSelected = selected.includes(itemId);

            if (tab === "scooters") {
              return (
                <Card
                  key={itemId}
                  className={`cursor-pointer transition-all duration-200 hover:border-primary/50 ${
                    isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border"
                  }`}
                  onClick={() => toggleSelect(itemId)}
                  data-testid={`item-card-${itemId}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="pt-1">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(itemId)} />
                      </div>
                      <div className="w-14 h-14 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                        {item.photos?.[0] ? (
                          <img src={item.photos[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Bike className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-primary">{itemId}</p>
                        <p className="font-semibold text-sm truncate">{item.brand} {item.model}</p>
                        <p className="text-xs text-muted-foreground">{item.year} | {item.mileage?.toLocaleString()} km | {item.vin ? truncate(item.vin, 12) : ""}</p>
                        <p className="text-sm font-bold text-primary mt-0.5">{item.selling_price?.toLocaleString()} EUR</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }

            // Parts card
            return (
              <Card
                key={itemId}
                className={`cursor-pointer transition-all duration-200 hover:border-primary/50 ${
                  isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border"
                }`}
                onClick={() => toggleSelect(itemId)}
                data-testid={`item-card-${itemId}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="pt-1">
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(itemId)} />
                    </div>
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                      {item.photos?.[0] ? (
                        <img src={item.photos[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <Wrench className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-primary">{itemId}</p>
                      <p className="font-semibold text-sm truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.category} | Qté: {item.quantity}</p>
                      <p className="text-sm font-bold text-primary mt-0.5">{item.price?.toLocaleString()} EUR</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        )}

        {filteredItems.length === 0 && tab !== "sales" && (
          <div className="text-center py-12 text-muted-foreground">
            Niciun articol gasit
          </div>
        )}

        {/* Sales Report Tab */}
        {tab === "sales" && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" /> Raport scutere vandute
              </h3>
              <p className="text-sm text-muted-foreground">
                Selectati perioada si descarcati PDF cu lista scuterelor vandute, date client, adresa livrare, RAR.
              </p>
              <div className="flex gap-3 items-end flex-wrap">
                <div className="space-y-1">
                  <label className="text-xs font-medium">De la:</label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-44" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium">Pana la:</label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-44" />
                </div>
                <Button onClick={handleSalesReport} disabled={!dateFrom || !dateTo}>
                  <FileText className="w-4 h-4 mr-2" /> Genereaza raport PDF
                </Button>
              </div>
              <div className="text-xs text-muted-foreground bg-secondary/30 rounded-lg p-3 space-y-1">
                <p>Raportul include:</p>
                <p>• Nume scuter, an, nr. produs</p>
                <p>• Nume client, telefon</p>
                <p>• Adresa de livrare</p>
                <p>• Data RAR si daca a fost acceptat/refuzat</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
